// Season tagging for sheet rows. A production season runs 1 Sep → 14 Aug and is labelled "25-26".
// The Service sheet isn't season-scoped, so we work out each vessel's season from, in order:
//   1. an explicit Season / year column on the sheet, if there is one;   → source 'column'
//   2. the build season the server resolved from the boat's SAP / name.  → source 'build'
//   3. a date column on the row (delivery / service date), via seasonIdFor. → source 'date'
// The SOURCE matters: a 'build' season is only the year the boat was made, which is NOT when it's being
// serviced — so the page shows it as a tag but must never archive a current-service boat just because it
// was built in an older season. Only an explicit service-season signal ('column' / 'date') should
// archive. Everything is defensive so a sheet without any season signal simply shows no tags.

import { seasonIdFor } from '@technohull/shared';
import type { SheetGridColumn, SheetGridRow } from './hooks.js';

/** Normalise a free-text season / year value to a canonical "YY-YY" id, or null if it isn't one.
 *  Accepts "26-27", "2026-27", "2026-2027", "26/27", "2026/27", "Season 26-27", a lone "2026" or "26". */
export function normalizeSeasonId(raw: string | boolean | undefined | null): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s) return null;
  const pair = s.match(/(\d{2,4})\s*[-/–—]\s*(\d{2,4})/);
  if (pair) {
    const a = Number(pair[1]) % 100;
    const b = Number(pair[2]) % 100;
    return `${String(a).padStart(2, '0')}-${String(b).padStart(2, '0')}`;
  }
  const year = s.match(/\b(\d{4})\b/);
  if (year) {
    const y = Number(year[1]) % 100;
    return `${String(y).padStart(2, '0')}-${String((y + 1) % 100).padStart(2, '0')}`;
  }
  const two = s.match(/\b(\d{2})\b/);
  if (two) {
    const y = Number(two[1]);
    return `${String(y).padStart(2, '0')}-${String((y + 1) % 100).padStart(2, '0')}`;
  }
  return null;
}

/** A column that explicitly names the season (English or Greek), if the sheet has one. Kept strict so
 *  an unrelated date-ish column like "Model Year" or "Warranty Year" can't be mistaken for the season
 *  (it would otherwise override the authoritative build season). */
export function detectSeasonColumn(columns: SheetGridColumn[]): SheetGridColumn | undefined {
  return columns.find((c) => {
    const t = c.title.trim().toLowerCase();
    return /season|σεζ[οό]ν|σαιζ[οό]ν/.test(t) || t === 'year' || t === 'έτος';
  });
}

/** A date column to fall back on — prefer a delivery / service date, else the first date column. */
export function detectDateColumn(columns: SheetGridColumn[]): SheetGridColumn | undefined {
  const dateCols = columns.filter((c) => c.type === 'DATE' || c.type === 'DATETIME');
  return (
    dateCols.find((c) => /deliver|service|σέρβις|παράδοση|ημερομ/i.test(c.title)) ??
    dateCols.find((c) => /date|ημερομ/i.test(c.title)) ??
    dateCols[0]
  );
}

/** where a resolved season came from — `build` is the boat's make-year and must not archive a row. */
export type SeasonSource = 'column' | 'build' | 'date';
export interface RowSeason {
  id: string;
  source: SeasonSource;
}

/** Resolve one row's season (or null) using the detected columns + any server-provided build season,
 *  tagging where it came from so the caller can treat a build-season tag as non-archiving. */
export function resolveRowSeason(
  row: SheetGridRow,
  seasonCol: SheetGridColumn | undefined,
  dateCol: SheetGridColumn | undefined,
): RowSeason | null {
  if (seasonCol) {
    const fromCol = normalizeSeasonId(row.cells[seasonCol.id]);
    if (fromCol) return { id: fromCol, source: 'column' };
  }
  if (row.season) return { id: row.season, source: 'build' };
  if (dateCol) {
    const v = row.cells[dateCol.id];
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return { id: seasonIdFor(d), source: 'date' };
    }
  }
  return null;
}

/** Sort season ids chronologically ("25-26" before "26-27"). */
export function compareSeasonIds(a: string, b: string): number {
  const na = Number(a.split('-')[0]);
  const nb = Number(b.split('-')[0]);
  return na - nb;
}
